import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, LegalDocumentItem } from '../services/api';
import { C } from '../components/UI';
import { useBranding } from '../context/BrandingContext';

const EFFECTIVE_DATE = 'August 20, 2026';

export default function TermsOfService() {
    const navigate = useNavigate();
    const branding = useBranding();
    const navText = branding.navTextColor ?? '#ffffff';
    const COMPANY = branding.appName || 'BoxRunner';
    const EMAIL = branding.supportEmail || 'support@box-runner.net';

    // Admin-edited copy (Admin Dashboard > Disclosures > Site Legal Pages) takes over when
    // present; the sections below remain as the fallback for a tenant that hasn't customized it.
    const [doc, setDoc] = useState<LegalDocumentItem | null>(null);
    useEffect(() => { api.getLegalDocument('TermsOfService').then(setDoc).catch(() => {}); }, []);

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif", color: C.text }}>
            {/* Header */}
            <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56 }}>
                {branding.logoUrl
                    ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 120, objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate(-1)} />
                    : <span style={{ fontSize: 18, fontWeight: 700, color: navText, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => navigate(-1)}>
                        {branding.appName}
                      </span>
                }
                <span style={{ color: navText, fontSize: 13, opacity: 0.7 }}>· Terms of Service</span>
                <button
                    onClick={() => navigate(-1)}
                    style={{ marginLeft: 'auto', background: 'transparent', border: `1px solid ${navText}`, color: navText, borderRadius: 6, padding: '4px 12px', fontSize: 13, cursor: 'pointer', opacity: 0.8 }}
                >
                    ← Back
                </button>
            </div>

            {/* Content */}
            <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 80px' }}>
                {doc ? (
                    <>
                        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>{doc.title}</h1>
                        <p style={{ color: C.textSub, fontSize: 14, marginBottom: 40 }}>Updated {new Date(doc.updatedAt).toLocaleDateString()}</p>
                        <div style={{ fontSize: 14, lineHeight: 1.75, color: C.text, whiteSpace: 'pre-wrap' }}>{doc.body}</div>
                    </>
                ) : (
                <>
                <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>Terms of Service</h1>
                <p style={{ color: C.textSub, fontSize: 14, marginBottom: 40 }}>Effective {EFFECTIVE_DATE}</p>

                <Section title="1. Agreement to Terms">
                    <p>These Terms of Service ("Terms") govern your access to and use of the {COMPANY} website, mobile app, and related services (collectively, the "Service"), operated by {COMPANY} ("we," "us," or "our"). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>
                </Section>

                <Section title="2. The Service">
                    <p>{COMPANY} operates an online platform that connects customers seeking junk-removal and related pickup services with independent, third-party contractors who perform those services. {COMPANY} is not itself a moving, hauling, or waste-removal company — we facilitate the connection, scheduling, and payment between customers and contractors, who operate as independent businesses and are solely responsible for the services they perform.</p>
                </Section>

                <Section title="3. Accounts">
                    <p>You must provide accurate, current information when creating an account and keep it up to date. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You must be at least 18 years old to create an account.</p>
                </Section>

                <Section title="4. Bookings, Pricing, and Payment">
                    <p>When you book a service through the platform, you authorize us (via our payment processor, Stripe) to charge your payment method for the quoted amount, any applicable service fee and tax, and any tip you elect to add. Final pricing may be adjusted from the initial estimate based on the actual scope of work performed, as disclosed to you before charges are finalized. Cancellation and refund terms are as presented to you at the time of booking.</p>
                </Section>

                <Section title="5. Contractor Relationship">
                    <p>Contractors on the platform are independent contractors, not employees or agents of {COMPANY}. {COMPANY} does not control the manner or means by which contractors perform services. While we take steps such as background checks and ratings to help maintain platform quality, we do not guarantee the conduct, availability, or workmanship of any contractor.</p>
                </Section>

                <Section title="6. Acceptable Use">
                    <ul>
                        <li>Do not use the Service for any unlawful purpose or in violation of any applicable law or regulation.</li>
                        <li>Do not attempt to interfere with, disrupt, or gain unauthorized access to the Service or its systems.</li>
                        <li>Do not use the Service to harass, defraud, or harm another user or contractor.</li>
                        <li>Do not misrepresent your identity or the nature of a booking.</li>
                    </ul>
                </Section>

                <Section title="7. Third-Party Integrations">
                    <p>The Service may connect with third-party platforms (for example, payment processors or lead-source partners) to provide certain features. Your use of those third-party services is subject to their own terms, and {COMPANY} is not responsible for their content, availability, or practices.</p>
                </Section>

                <Section title="8. Disclaimers and Limitation of Liability">
                    <p>The Service is provided "as is" without warranties of any kind, express or implied. To the fullest extent permitted by law, {COMPANY} is not liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, or from the acts or omissions of any contractor or customer. Our total liability for any claim arising from the Service is limited to the amount you paid us in the six months preceding the claim.</p>
                </Section>

                <Section title="9. Dispute Resolution">
                    <p>If a dispute arises between you and {COMPANY}, we encourage you to first contact us at <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a> so we can try to resolve it informally. Disputes that cannot be resolved informally may be subject to binding arbitration on an individual basis, as further described in any separate arbitration agreement presented to you (for example, during contractor onboarding).</p>
                </Section>

                <Section title="10. Termination">
                    <p>We may suspend or terminate your access to the Service at any time, with or without notice, for conduct that violates these Terms or is otherwise harmful to other users, contractors, or the Service. You may stop using the Service and close your account at any time.</p>
                </Section>

                <Section title="11. Changes to These Terms">
                    <p>We may update these Terms from time to time. If we make material changes, we will notify you by email or by displaying a notice in the app before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.</p>
                </Section>

                <Section title="12. Governing Law">
                    <p>These Terms are governed by the laws of the State of Alabama, without regard to its conflict-of-laws principles, unless otherwise required by applicable law.</p>
                </Section>

                <Section title="13. Contact Us">
                    <p>If you have questions about these Terms, please contact us:</p>
                    <p style={{ marginTop: 8 }}>
                        <strong>{COMPANY}</strong><br />
                        Email: <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a>
                    </p>
                </Section>
                </>
                )}
            </div>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.green, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>{title}</h2>
            <div style={{ fontSize: 14, lineHeight: 1.75, color: C.text, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {children}
            </div>
        </section>
    );
}
